// Tema de un sitio público -> atributos de la raíz (ver site.css). Solo
// valores acotados: estilo y redondeado de una lista cerrada y un color
// primario en hexadecimal (se vuelve a validar aquí).

import type { CSSProperties } from "react";

export type SitePreset = "institucional" | "comercial" | "heskala";
export type SiteRadius = "none" | "sm" | "md" | "lg";

export interface SiteTheme {
  preset: string;
  primaryColor?: string;
  logo?: string;
  radius?: string;
}

const PRESETS: readonly SitePreset[] = ["institucional", "comercial", "heskala"];
const RADII: readonly SiteRadius[] = ["none", "sm", "md", "lg"];
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** #abc -> [r, g, b] (0-255). */
function rgbOf(hex: string): [number, number, number] {
  const h = hex.slice(1);
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

/** Luminancia relativa (WCAG 2.x) de un color hexadecimal. */
function luminanceOf(hex: string): number {
  const [r, g, b] = rgbOf(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const isHexColor = (value: string): boolean => HEX_RE.test(value);

/** Contraste WCAG entre dos colores hexadecimales (de 1 a 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminanceOf(a), luminanceOf(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Mínimo de WCAG AA para texto normal. */
export const MIN_TEXT_CONTRAST = 4.5;

/** Contraste del color con texto blanco y si alcanza para leerse (AA, 4,5:1). */
export function whiteTextContrast(hex: string): { ratio: number; ok: boolean } {
  const ratio = contrastRatio(hex, "#ffffff");
  // Se muestra truncado: 4.49 se ve «4.4:1» (redondeado diría 4.5 y no alcanza).
  return { ratio: Math.floor(ratio * 10) / 10, ok: ratio >= MIN_TEXT_CONTRAST };
}

/** Texto blanco o casi negro según el contraste con el color (WCAG). */
export function readableOn(hex: string): "#ffffff" | "#111111" {
  const luminance = luminanceOf(hex);
  // Contraste contra blanco vs. contra #111 (luminancia ~0.0056).
  return (1.05 / (luminance + 0.05) >= (luminance + 0.05) / 0.0556) ? "#ffffff" : "#111111";
}

export interface SiteRootProps {
  className: string;
  "data-preset": SitePreset;
  "data-radius"?: SiteRadius;
  style?: CSSProperties;
}

/** Clase, atributos y variables de la raíz del sitio. */
export function siteRootProps(theme: SiteTheme | null | undefined): SiteRootProps {
  const preset = PRESETS.includes(theme?.preset as SitePreset) ? (theme!.preset as SitePreset) : "heskala";
  const radius = RADII.includes(theme?.radius as SiteRadius) ? (theme!.radius as SiteRadius) : undefined;
  const color = theme?.primaryColor && HEX_RE.test(theme.primaryColor) ? theme.primaryColor : undefined;
  return {
    className: preset === "heskala" ? "public-site" : "public-site site-light",
    "data-preset": preset,
    ...(radius ? { "data-radius": radius } : {}),
    ...(color
      ? {
          style: {
            "--primary": color,
            "--primary-foreground": readableOn(color),
            "--ring": color,
          } as CSSProperties,
        }
      : {}),
  };
}

/** Logo que se puede mostrar (solo https; el servidor ya resuelve archivos subidos). */
export const safeLogo = (logo: string | undefined): string | undefined =>
  logo && /^https:\/\//.test(logo) ? logo : undefined;
